"use client"

import { useEffect, useState } from "react"
import { FAB_ACTIONS_ATTR, subscribeToBottomActions } from "@/hooks/use-fab-bottom-actions"
import type { FabCorner } from "@/hooks/use-fab-corner"
import { sidebarSide } from "@/hooks/use-fab-sidebar-inset"
import type { TextDirection } from "@/lib/i18n/direction"

/**
 * Keeping the feedback launcher off the action bar a page actually has on screen (QA re-walk, 17 Oct 2026, re-verify R2).
 *
 * A page whose primary buttons run along the bottom marks the bar `data-fab-actions` (use-fab-auto-corner.ts), and the launcher
 * used to rest one FIXED band higher (4.5rem) while any such marker existed. That is right only for a bar pinned 24 px above the
 * window's edge. The survey builder's footer is in the page's flow: on a short page it sits wherever the content ends (86 px up
 * on the QA draft), on a long one it passes through the launcher's corner as the person scrolls, and a fixed lift put the
 * launcher on "Next" in the first case ("fab-stack-root intercepts pointer events").
 *
 * So the launcher is placed by where the bar IS. A bar is a marked element with a size of its own that is not the page itself
 * (at most BAR_MAX_HEIGHT_PX tall). The launcher stays at its resting corner unless that rectangle, with a gap round it, would
 * cover a bar; then it sits a gap above the bar, and it goes back to rest once the bar has passed. It re-reads on scroll (any
 * scroll container: the listener is a capture one), on resize, and whenever the page changes (the same observer that tells the
 * host a marker came or went). A marker with no rectangle to measure (a page-wide wrapper, an element not laid out yet) keeps
 * the fixed band, so a page that marks itself loosely is cleared as before.
 */

/** The launcher is 48 px square and rests 1.5rem (24 px) in from its corner (FAB_CORNER_TRANSFORM). */
export const FAB_SIZE_PX = 48
export const FAB_MARGIN_PX = 24
/** The air kept between the launcher and a bar. */
export const FAB_BAR_GAP_PX = 12
/** A marked element taller than this is a page, not a bar: it cannot be measured against, and the fixed band stands. */
export const BAR_MAX_HEIGHT_PX = 160

export interface PxRect {
  left: number
  top: number
  right: number
  bottom: number
}

/** Where the launcher rests, in px from the window's top-left, for a corner and the room the sidebar holds (fabCornerTransformFor). */
export function fabRestRect(
  corner: FabCorner,
  insetPx: number,
  direction: TextDirection,
  viewport: { width: number; height: number },
): PxRect {
  const side = corner.endsWith("right") ? "right" : "left"
  const inset = insetPx > 0 && side === sidebarSide(direction) ? insetPx : 0
  const left = side === "left" ? FAB_MARGIN_PX + inset : viewport.width - FAB_MARGIN_PX - FAB_SIZE_PX - inset
  const top = corner.startsWith("bottom") ? viewport.height - FAB_MARGIN_PX - FAB_SIZE_PX : FAB_MARGIN_PX
  return { left, top, right: left + FAB_SIZE_PX, bottom: top + FAB_SIZE_PX }
}

/**
 * How many px a BOTTOM corner must rise so the launcher sits a gap above every bar it would cover; 0 when none is in the way
 * (below the window, above the launcher, off to the side) and always 0 at a top corner, where there is no bar to clear.
 */
export function barLiftPx(corner: FabCorner, rest: PxRect, bars: readonly PxRect[]): number {
  if (!corner.startsWith("bottom")) return 0
  let lift = 0
  for (const bar of bars) {
    const sideBySide = bar.left < rest.right + FAB_BAR_GAP_PX && bar.right > rest.left - FAB_BAR_GAP_PX
    const inTheWay = bar.bottom > rest.top - FAB_BAR_GAP_PX && bar.top < rest.bottom + FAB_BAR_GAP_PX
    if (!sideBySide || !inTheWay) continue
    const top = bar.top - FAB_BAR_GAP_PX - FAB_SIZE_PX
    lift = Math.max(lift, rest.top - top)
  }
  return lift
}

/** The rectangles of the bars on screen now: marked elements with a size of their own, not the page. */
function barsOnScreen(): PxRect[] {
  const bars: PxRect[] = []
  for (const element of document.querySelectorAll(`[${FAB_ACTIONS_ATTR}]`)) {
    const r = element.getBoundingClientRect()
    if (r.width > 0 && r.height > 0 && r.height <= BAR_MAX_HEIGHT_PX) bars.push({ left: r.left, top: r.top, right: r.right, bottom: r.bottom })
  }
  return bars
}

/** The lift for the bars on screen now: null when there is no bar to measure (the fixed band, or nothing, applies). */
export function measureBarLift(corner: FabCorner, direction: TextDirection, insetPx: number): number | null {
  if (typeof document === "undefined" || typeof window === "undefined") return null
  const bars = barsOnScreen()
  if (bars.length === 0) return null
  const rest = fabRestRect(corner, insetPx, direction, { width: window.innerWidth, height: window.innerHeight })
  return barLiftPx(corner, rest, bars)
}

/**
 * The px the launcher must rise now to clear the bar the page has on screen, or null when no bar can be measured. Re-read at
 * most once per animation frame on scroll and resize, and when the page changes.
 */
export function useFabBarLift(corner: FabCorner, direction: TextDirection, insetPx: number): number | null {
  const [lift, setLift] = useState<number | null>(null)

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    let frame = 0
    const measure = () => {
      frame = 0
      const next = measureBarLift(corner, direction, insetPx)
      setLift((previous) => (previous === next ? previous : next))
    }
    const schedule = () => {
      if (frame === 0) frame = window.requestAnimationFrame(measure)
    }
    measure()
    // Capture: a page's own scroll container does not bubble its scroll to the window.
    window.addEventListener("scroll", schedule, { capture: true, passive: true })
    window.addEventListener("resize", schedule)
    const unsubscribe = subscribeToBottomActions(schedule)
    return () => {
      window.removeEventListener("scroll", schedule, { capture: true })
      window.removeEventListener("resize", schedule)
      unsubscribe()
      if (frame !== 0) window.cancelAnimationFrame(frame)
    }
  }, [corner, direction, insetPx])
  /* eslint-enable react-hooks/set-state-in-effect */

  return lift
}
